import { valueRender } from '@repo/toolkit/conditional-render'
import { useNavigator } from '@repo/toolkit/use-navigator'
import { Column } from '@repo/ui/data-table'
import { LocationTwoIcon, PlusIcon, UploadIcon } from '@repo/ui/icons'
import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'

import { API } from '../../api/apis.config'
import { getCountriesList, getCountriesListQueryKey } from '../../api/countries/get-countries-list'
import CountryFlag from '../../components/elements/country-flag'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import { JourneyPlacesFilter } from '../../components/pages/journey-places/journey-place-filter'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { accomodationEnumMapping } from '../../lib/types/enums/accomodation-type/accomodation-type'
import {
  JourneyPlace,
  JourneyPlaceFilters
} from '../../lib/types/models/journey-place/journey-place'
import {
  transformJourneyPlaceFiltersToUrl,
  transformUrlToJourneyPlaceFilters
} from '../../lib/utils/filters-mapping/journey-place'

const JourneyPlaceListPage = () => {
  const journeyPlaceInitialFilter: JourneyPlaceFilters = {}
  const { navigate, navigateWithEvent } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const tableRef = React.useRef<DynamicTableRef<JourneyPlace>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { modalState, toggleModal, closeModel } = useModalManager<'filters', JourneyPlace[]>([
    'filters'
  ])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<JourneyPlaceFilters>({
      initialFilters: journeyPlaceInitialFilter,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformJourneyPlaceFiltersToUrl,
      transformUrlToFilter: transformUrlToJourneyPlaceFilters
    })

  const { data: countryList } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })

  const columns: Column<JourneyPlace>[] = [
    {
      accessorKey: 'name',
      header: 'Lieu de séjour',
      enableSorting: true
    },
    {
      accessorKey: 'address',
      header: 'Adresse',
      enableSorting: true,
      cell: ({ row }) => valueRender(row.original.address)
    },
    {
      accessorKey: 'country.name',
      header: 'Pays',
      enableSorting: true,
      cell: ({ row }) => {
        if (!countryList) return '-'
        const { code, name } = row.original.country

        return <CountryFlag code={code} label={name} />
      }
    },
    {
      accessorKey: 'accomodation',
      header: "Type d'hébergement",
      enableSorting: true,
      cell: ({ row }) => accomodationEnumMapping[row.original.accomodation]
    }
  ]

  return (
    <div>
      <UserNavBar title={'Lieux de séjour'} icon={LocationTwoIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<JourneyPlace, JourneyPlaceFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title={'Tous les lieux de séjour'}
        deleteMessage={{ name: 'lieu de séjour', attributeName: 'name' }}
        columns={columns}
        selectable
        allowSearchParam
        onRowClick={(journeyPlace, event) =>
          navigateWithEvent(
            Paths.JOURNEY_PLACE_UPDATE_PAGE.replace(':id', journeyPlace.id.toString()),
            event
          )
        }
        endpoint={API.journeyPlaces.getAll.url}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            refetch: true,
            variant: 'primary',
            label: 'Ajouter un nouveau lieu',
            fn: () => navigate(Paths.JOURNEY_PLACE_CREATE_PAGE)
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Exporter',
            fn: (rows) => {
              const ids = rows.map((row) => row.id)
              downloadDocuments({
                endpoint: API.journeyPlaces.exportExcel.url,
                ids
              })
            }
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <JourneyPlacesFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}

export default JourneyPlaceListPage
